/**
 * TextbausteinPicker (docs/KONZEPT-VERSANDMODUS-UND-TEXTBAUSTEINE.md § 4.1): eine kleine,
 * wiederverwendbare Komponente — laedt GET /api/text-snippets ueber den bestehenden useApi-Hook
 * und zeigt eine durchsuchbare Liste (Titel + Kategorie-Chip). Ein Klick auf einen Eintrag ruft
 * `onInsert(content)` auf — ein reiner Callback, keine Kopplung an ein bestimmtes Formular/Feld
 * (der Aufrufer entscheidet, WIE/WO der Text eingefuegt wird, z.B. an der Cursor-Position eines
 * Compose-Textarea). "Eine Komponente, vier Einsatzorte" (§ 4.1) — Builder, Kostenuebernahme,
 * Rechnung, Erinnerungen (/freigaben) teilen sich denselben betriebsweiten Pool.
 *
 * Inaktive Bausteine (active: false) werden hier NICHT angezeigt — der Picker ist der
 * Einfuegeort fuer aktive, gepflegte Texte; inaktive verwalten sich im /wissen-Reiter
 * (TextbausteineTab), der auch inaktive sichtbar macht.
 */
import { useMemo, useState } from "react";
import { useApi } from "../hooks/useApi";
import { Icon } from "./components";

interface TextSnippet {
  id: string;
  title: string;
  content: string;
  category: string | null;
  active: boolean;
}

/**
 * B6 (UX-Polish 25.08.2026) — wohin der „Neuen Textbaustein anlegen"-CTA des leeren Zustands
 * fuehrt.
 *
 * DAS ZIEL, nachgeschlagen statt vermutet: der Auftrag sagt „in die Einstellungen", die Pflege
 * der Bausteine sitzt aber auf `/wissen`, Reiter „Textbausteine" (`pages/knowledge.tsx` TABS,
 * `pages/textbausteine-tab.tsx`) — dort und nirgends sonst legt man einen an. Der Reiter ist
 * deep-linkbar (`?tab=…`, `useSearchParams` in `knowledge.tsx`), deshalb traegt der Link den
 * Reiter-Schluessel mit; ohne ihn landete der Betreiber auf „Was MAESTRO weiss" und muesste
 * weitersuchen. Der Schluessel heisst `textbausteine` und nicht `bausteine` (der alte Wert lenkt
 * laut Kommentar dort auf einen leeren Reiter um).
 *
 * WARUM `<a target="_blank">` UND NICHT `<Link>`: der Picker sitzt in vier Zusammenhaengen, und
 * in allen vieren schreibt der Betreiber gerade an einer Nachricht (Angebot senden,
 * Kostenuebernahme, Rechnung, Erinnerungen). Ein Navigationswechsel im selben Tab wuerde diesen
 * ungespeicherten Entwurf verwerfen — genau deshalb steht `target="_blank"` im Auftrag. Ein
 * `<Link target="_blank">` von react-router laedt ohnehin voll neu; ein schlichtes `<a>` tut
 * dasselbe, haengt aber nicht an einem Router-Kontext. Das zaehlt hier, weil die Komponente
 * geteilt ist und ihr Unit-Test sie bewusst ohne Router rendert.
 */
export const TEXTBAUSTEINE_PFAD = "/gedaechtnis?tab=textbausteine";

/**
 * § 4.1: fuegt an der aktuellen Cursor-Position ein, nicht immer am Ende.
 *
 * Geteilter Helfer fuer jeden TextbausteinPicker-Einsatzort — Schritt 5/6 (Kostenuebernahme/
 * Rechnung, `event-detail.tsx`) hatten je eine lokale Kopie; Schritt 7 (`/freigaben`) braucht
 * denselben Helfer in einer weiteren Datei. Eine Fassung hier statt drei identischen Kopien, die
 * bei der naechsten Anpassung auseinanderlaufen wuerden.
 */
export function insertAtCursor(
  /*
   * Punkt 82 (22.09.2026): zusaetzlich `HTMLInputElement`. Die Platzhalter-Leiste der Mailtexte
   * fuegt auch in die BETREFFZEILE ein, und die ist ein einzeiliges Feld. Die Auswahl-API
   * (`selectionStart`/`setSelectionRange`) ist bei beiden dieselbe — nur der Typ war enger als
   * noetig. Rein additiv: jeder bestehende Aufrufer uebergibt weiterhin ein Textarea.
   */
  field: HTMLTextAreaElement | HTMLInputElement | null,
  current: string,
  setter: (v: string) => void,
  content: string,
): void {
  if (!field) {
    setter(current + content);
    return;
  }
  const start = field.selectionStart ?? current.length;
  const end = field.selectionEnd ?? current.length;
  const next = current.slice(0, start) + content + current.slice(end);
  setter(next);
  requestAnimationFrame(() => {
    field.focus();
    const pos = start + content.length;
    field.setSelectionRange(pos, pos);
  });
}

/**
 * Ruhiger Werkzeugknopf (Mockup Variante B „KI-Menü“, Antoine 25.10.2026): Textbaustein und KI-Menü
 * über dem Nachrichtenfeld im Senden-Dialog sehen exakt gleich aus. `RUHIGER_KNOPF_KI` färbt den
 * KI-Knopf nur leicht in der Akzentfarbe.
 */
export const RUHIGER_KNOPF =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-[9px] border border-outline-variant bg-surface-container-lowest px-2.5 py-1.5 text-[12px] font-semibold text-on-background transition-colors hover:bg-surface-container-high disabled:cursor-not-allowed disabled:opacity-50 dark:border-outline/40 dark:bg-night-card dark:text-inverse-on-surface dark:hover:bg-white/5";
export const RUHIGER_KNOPF_KI = "!border-transparent !bg-primary/8 !text-primary hover:!bg-primary/14 dark:hover:!bg-primary/20";

export function TextbausteinPicker({ onInsert, ruhig = false }: { onInsert: (content: string) => void; ruhig?: boolean }) {
  const [open, setOpen] = useState(false);
  const [suche, setSuche] = useState("");
  const { data, loading, error } = useApi<TextSnippet[]>(open ? "/api/text-snippets" : null);

  const bausteine = useMemo(() => {
    const alle = (data ?? []).filter((b) => b.active);
    const q = suche.trim().toLowerCase();
    if (!q) return alle;
    return alle.filter((b) => b.title.toLowerCase().includes(q) || (b.category ?? "").toLowerCase().includes(q));
  }, [data, suche]);

  const ersterBaustein = !loading && !error && !suche.trim() && bausteine.length === 0;

  return (
    <div className={ruhig ? "inline-block" : "relative inline-block"}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ruhig ? "Textbaustein einfügen" : undefined}
        className={
          ruhig
            ? RUHIGER_KNOPF
            : "inline-flex items-center gap-1.5 rounded-full border border-outline-variant/60 bg-surface-container-lowest px-3 py-1.5 font-label-md text-[12.5px] font-medium text-on-surface-variant transition-colors hover:bg-surface-container-high dark:border-outline/30 dark:hover:bg-white/5"
        }
      >
        <Icon name={ruhig ? "text_snippet" : "bookmark_add"} className="text-[16px]" />
        {ruhig ? "Textbaustein" : "Textbaustein einfügen"}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="listbox"
            aria-label="Textbausteine"
            className={`absolute ${ruhig ? "right-0" : "left-0"} top-[calc(100%+4px)] z-50 w-[340px] max-w-[90vw] overflow-hidden rounded-xl border border-outline-variant/40 bg-surface-container-lowest shadow-xl dark:border-outline/30 dark:bg-night-card`}
          >
            <div className="border-b border-outline-variant/30 p-2 dark:border-outline/30">
              <input
                type="text"
                autoFocus
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Textbaustein suchen …"
                className="w-full rounded-lg bg-surface-container-high px-2.5 py-1.5 text-[13px] text-on-surface outline-none placeholder:text-on-surface-variant/70 dark:bg-white/5 dark:text-inverse-on-surface"
              />
            </div>
            <div className="max-h-[280px] overflow-y-auto py-1">
              {loading ? (
                <p className="px-3 py-4 text-center font-body-sm text-body-sm text-on-surface-variant">Lädt …</p>
              ) : error ? (
                <p className="px-3 py-4 text-center font-body-sm text-body-sm text-error">{error}</p>
              ) : bausteine.length === 0 ? (
                <div className="px-3 py-4 text-center">
                  <p className="font-body-sm text-body-sm text-on-surface-variant/70">
                    {suche.trim() ? "Keine Treffer." : "Noch keine Textbausteine angelegt."}
                  </p>
                </div>
              ) : (
                bausteine.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => {
                      onInsert(b.content);
                      setOpen(false);
                      setSuche("");
                    }}
                    className="flex w-full items-start justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-surface-container-high dark:hover:bg-white/5"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium text-on-surface dark:text-inverse-on-surface">{b.title}</span>
                      <span className="block truncate font-body-sm text-[11.5px] text-on-surface-variant/80">{b.content}</span>
                    </span>
                    {b.category && (
                      <span className="shrink-0 rounded-md bg-surface-container-high px-1.5 py-0.5 font-label-md text-[10.5px] text-on-surface-variant dark:bg-white/5">
                        {b.category}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
            {/* Feste Fußzeile (Antoine, 25.10.2026): direkter Weg zur Pflege der Bausteine, auch im
                leeren Zustand (dort als „Ersten Textbaustein anlegen“, B6 25.08.2026 — der frühere
                Link im Leer-Hinweis ist hierher gewandert). NEUER TAB, weil im Formular ein
                ungespeicherter Nachrichtenentwurf liegt; bei „Keine Treffer“ bleibt es „verwalten“,
                wer gesucht hat, will filtern. */}
            <a
              href={TEXTBAUSTEINE_PFAD}
              target="_blank"
              rel="noreferrer"
              data-testid={ersterBaustein ? "textbaustein-picker-anlegen" : "textbaustein-picker-verwalten"}
              className="flex items-center gap-2 border-t border-outline-variant/30 px-3 py-2.5 text-[13px] font-medium text-on-surface transition-colors hover:bg-surface-container-high dark:border-outline/30 dark:text-inverse-on-surface dark:hover:bg-white/5"
            >
              <Icon name={ersterBaustein ? "add" : "edit_note"} className="text-[18px] text-on-surface-variant/70" />
              <span className="min-w-0 flex-1">{ersterBaustein ? "Ersten Textbaustein anlegen" : "Textbausteine verwalten"}</span>
              <Icon name="open_in_new" className="text-[15px] text-on-surface-variant/60" />
            </a>
          </div>
        </>
      )}
    </div>
  );
}
